"use client";

import Link from "next/link";
import posthog from "posthog-js";
import { useEffect, useRef, useState } from "react";
import { MicroSurvey, shouldAskExitSurvey } from "@/components/MicroSurvey";
import { PREMIUM_PRICE_LABEL } from "@/lib/portfolio/entitlements";

/**
 * "Create a video application" — the job page's conversion driver (CM-22).
 *
 * A video speaks louder than a CV: for the job in view we match the applicant's
 * profile to the description and hand them customized talking points, then they
 * record and attach the link to their cover letter. That whole flow is Premium,
 * so this modal is the free → Premium moment — and Premium also unlocks the
 * changemaker portfolio page, so we sell both benefits together.
 *
 * Two modes off one prop (`isPaid`): paid users get a direct "start" that lands
 * on the wizard pre-seeded with this job; everyone else gets the upgrade pitch.
 * Modeled on PortfolioPitch: native <dialog> (Esc + backdrop close free), the
 * shared passport-frame `pitch-dialog` styling, and the same intro thumbnail.
 * All analytics are consent-gated via posthog.__loaded.
 */
export function VideoApplicationCta({
  jobId,
  jobTitle,
  orgName,
  applyUrl,
  isPaid,
  isAuthed,
  /** CM-460: pop the dialog itself instead of waiting for the trigger button
   * click — the post-apply surface reveals this whole component on return-to-
   * tab, so by the time a visitor could see the button the dialog should
   * already be open. Fires once per `true` transition, never on every render. */
  autoOpen,
  /** CM-460: lets a caller (PostApplyAlert) keep its own CTA-click event
   * alongside this component's native ones, so the two surfaces stay
   * separately queryable. */
  onCtaClick,
}: {
  jobId: number;
  jobTitle: string;
  orgName: string;
  applyUrl: string;
  isPaid: boolean;
  isAuthed: boolean;
  autoOpen?: boolean;
  onCtaClick?: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const autoOpenedRef = useRef(false);
  // CM-460's auto-open on the post-apply surface and this component's own
  // click-triggered button both call the same open()/captureCta(), which
  // blended two different intents into one video_app_pitch_opened/
  // _cta_clicked series — an auto-open is an impression nobody asked for,
  // a click-triggered open is proven intent, and averaging them together
  // is exactly what would make CM-460's since-ship CTR unreadable against
  // the pre-CM-460 36% benchmark (measured on click-triggered opens only).
  const triggerRef = useRef<"click" | "auto">("click");
  // True once any CTA inside this open has been clicked — read by the
  // dialog's native `close` handler below to tell "closed after clicking
  // through" from "closed without converting" (CM-adjacent to first_save_intent/
  // apply_click_feedback: we fire open/cta_clicked but never learn WHY someone
  // declines).
  const ctaClickedRef = useRef(false);
  const [showDeclineSurvey, setShowDeclineSurvey] = useState(false);

  // One href for every state, as the page itself (`/dashboard/applications/new`)
  // already handles the anon→login and non-member→UpgradeWall redirects, with
  // jobUrl/role/company preserved through both. Routing unpaid users to a
  // different, generic upgrade target instead (as this briefly did) drops that
  // pre-seeding — a signed-in non-member landed on a blank upgrade page with no
  // memory of which job they'd just applied to, and an anon visitor who logged
  // in came back to the job page, not the wizard, same regression CM-439 wrote
  // this pre-seeding to avoid in the first place.
  const createHref =
    `/dashboard/applications/new?jobUrl=${encodeURIComponent(applyUrl)}` +
    `&role=${encodeURIComponent(jobTitle)}&company=${encodeURIComponent(orgName)}`;

  const open = (trigger: "click" | "auto") => {
    triggerRef.current = trigger;
    ctaClickedRef.current = false;
    dialogRef.current?.showModal();
    if (posthog.__loaded)
      posthog.capture("video_app_pitch_opened", {
        job_id: jobId,
        is_paid: isPaid,
        is_authed: isAuthed,
        trigger,
      });
  };
  const close = () => dialogRef.current?.close();
  const captureCta = (cta: string) => {
    ctaClickedRef.current = true;
    if (posthog.__loaded)
      posthog.capture("video_app_pitch_cta_clicked", {
        job_id: jobId,
        is_paid: isPaid,
        cta,
        trigger: triggerRef.current,
      });
    onCtaClick?.();
  };

  useEffect(() => {
    if (!autoOpen || autoOpenedRef.current) return;
    autoOpenedRef.current = true;
    open("auto");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpen]);

  // The native `close` event fires for every close path alike (the × button,
  // Escape, our own backdrop-click handler calling .close()) — one listener
  // here catches all of them instead of duplicating the check at each call
  // site. Only asks once per device ever (shouldAskExitSurvey), and only when
  // the dialog closed WITHOUT a CTA click — clicking through navigates away,
  // so there's nothing to ask.
  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    const onNativeClose = () => {
      if (ctaClickedRef.current) return;
      if (shouldAskExitSurvey("video_pitch_decline")) setShowDeclineSurvey(true);
    };
    el.addEventListener("close", onNativeClose);
    return () => el.removeEventListener("close", onNativeClose);
  }, []);

  const STEPS: [string, string][] = [
    ["01", "We match your profile to this job description."],
    ["02", "Get customized bullets on exactly what to say."],
    ["03", "Record a short video, attach the link to your cover letter."],
  ];

  return (
    <>
      <button
        type="button"
        onClick={() => open("click")}
        aria-haspopup="dialog"
        className="flex h-11 w-full items-center justify-center gap-2 rounded-md bg-ink text-sm font-semibold text-white transition-colors hover:bg-neutral-800"
      >
        <PlayIcon />
        Create a video application
      </button>

      <dialog
        ref={dialogRef}
        className="pitch-dialog"
        aria-labelledby="video-app-title"
        onClick={(e) => {
          if (e.target === dialogRef.current) close();
        }}
      >
        <div className="overflow-hidden rounded-xl border-2 border-ink bg-white">
          <div className="relative">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/images/video-intro-thumb.jpg"
              alt=""
              className="h-36 w-full object-cover sm:h-40"
            />
            <span aria-hidden className="absolute inset-0 bg-ink/20" />
            <span className="stamp stamp-tilt absolute left-4 top-4 bg-white/95 text-brand-700">
              Premium
            </span>
            <span
              aria-hidden
              className="absolute bottom-3 left-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/95 text-ink shadow-sm"
            >
              <PlayIcon size={18} />
            </span>
            <button
              type="button"
              onClick={close}
              aria-label="Close"
              className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white/95 text-lg leading-none text-ink shadow-sm transition-colors hover:bg-white"
            >
              ×
            </button>
          </div>

          <div className="p-6 sm:p-7">
            <p className="telex flex items-center gap-2 text-[9.5px] text-brand-700">
              <span aria-hidden className="inline-block h-[6px] w-[6px] bg-current" />
              Video application
            </p>
            <h2
              id="video-app-title"
              className="display-calm mt-3 text-2xl font-semibold leading-tight text-ink"
            >
              A video speaks louder than a CV.
            </h2>
            <p className="mt-3 text-[14.5px] leading-relaxed text-neutral-600">
              Hundreds of CVs read the same. Put a face and a voice on your
              application to{" "}
              <span className="font-medium text-ink">{jobTitle}</span>
              {orgName ? (
                <>
                  {" "}
                  at <span className="font-medium text-ink">{orgName}</span>
                </>
              ) : null}{" "}
              — tailored to this exact role, not a generic reel.
            </p>

            <ol className="mt-5 space-y-2.5">
              {STEPS.map(([n, t]) => (
                <li key={n} className="flex gap-3">
                  <span className="telex mt-0.5 shrink-0 text-brand-600">{n}</span>
                  <span className="text-[13.5px] leading-relaxed text-neutral-700">
                    {t}
                  </span>
                </li>
              ))}
            </ol>

            {isPaid ? (
              <>
                <div className="mt-6">
                  <Link
                    href={createHref}
                    onClick={() => captureCta("create")}
                    className="flex h-11 w-full items-center justify-center gap-2 rounded-md bg-brand-600 text-sm font-semibold text-white transition-colors hover:bg-brand-700"
                  >
                    Start your video application →
                  </Link>
                </div>
                <p className="telex mt-4 text-center text-[8.5px] text-neutral-400">
                  Included with your Premium plan
                </p>
              </>
            ) : (
              <>
                <div className="mt-6 rounded-lg border border-brand-100 bg-brand-50/60 p-3.5">
                  <p className="text-[13px] leading-relaxed text-neutral-700">
                    <span className="font-semibold text-ink">Premium</span> unlocks
                    tailored video applications{" "}
                    <span className="text-neutral-500">and</span> your own
                    changemaker portfolio page — one link that carries your
                    projects, record, and video into every application.
                  </p>
                </div>
                <div className="mt-5">
                  <Link
                    href={createHref}
                    onClick={() => captureCta(isAuthed ? "upgrade" : "signin")}
                    className="flex h-11 w-full items-center justify-center gap-2 rounded-md bg-brand-600 text-sm font-semibold text-white transition-colors hover:bg-brand-700"
                  >
                    {isAuthed
                      ? `Go Premium — only ${PREMIUM_PRICE_LABEL}`
                      : "Sign up to get started"}
                  </Link>
                </div>
                <p className="telex mt-4 text-center text-[8.5px] text-neutral-400">
                  {isAuthed
                    ? `Premium · ${PREMIUM_PRICE_LABEL} · cancel anytime`
                    : `Membership · ${PREMIUM_PRICE_LABEL}`}
                </p>
              </>
            )}
          </div>
        </div>
      </dialog>

      {showDeclineSurvey ? (
        <MicroSurvey
          surveyId="video_pitch_decline"
          question="What stopped you from creating a video application?"
          placeholder="e.g. not sure how it works, camera-shy, checking with my team…"
          quickOption={{ label: "Not comfortable on camera", answer: "Not comfortable on camera" }}
          onClose={() => setShowDeclineSurvey(false)}
        />
      ) : null}
    </>
  );
}

function PlayIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden fill="currentColor">
      <path d="M5 3.5v9a.5.5 0 0 0 .77.42l7-4.5a.5.5 0 0 0 0-.84l-7-4.5A.5.5 0 0 0 5 3.5Z" />
    </svg>
  );
}
